import React from 'react';
import EstimatingGuidance from '../../../components/EstimatingGuidance';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHammer, faRuler, faCalculator, faFileInvoiceDollar, faBullseye, faClock, faCogs, faCoins, faHardHat, faTools, faBriefcase, faBuilding, faDraftingCompass, faChartLine, faArrowRightLong, faCheckCircle, faCalendarCheck } from '@fortawesome/free-solid-svg-icons';
import '../../../styles/services/ServicePage.css';

const HERO_IMG = 'https://realestimateservice.com/wp-content/uploads/2022/06/2-1-1280x720.jpg';

const OFFERS = [
  { icon: faHammer, number: '01', title: 'Comprehensive Material Takeoff', desc: 'Our team meticulously analyses your blueprints to provide a detailed breakdown of every lumber component needed.', items: ['Dimensional lumber species consideration', 'Engineered wood products (LVL, I-joists, LSL, Parallam, Glulam)', 'Plywood, OSB, and sheathing materials', 'Immaculate calculation for Simpson Hardware'] },
  { icon: faRuler, number: '02', title: 'Accurate Quantity Calculation', desc: 'We provide a precise count of every piece of lumber needed, reducing waste and keeping your project on budget.', items: ['Piece-by-piece counts', 'Generic estimations (Square Feet, Linear Feet)'] },
  { icon: faCalculator, number: '03', title: 'Budgeting & Quotation Support', desc: 'Understanding the financial side of material procurement is essential for successful project management.', items: ['Create accurate project quotations', 'Plan for material costs upfront', 'Manage resources to avoid overspending'] },
  { icon: faFileInvoiceDollar, number: '04', title: 'Invoicing & Material Cost Tracking', desc: 'We streamline complex material expense tracking with clear, organized invoicing services.', items: ['Itemized lists of all lumber materials', 'Pricing breakdown for easy budget tracking', 'Ongoing updates throughout the project'] },
];

const WHY_US = [
  { icon: faBullseye, title: 'Precision & Accuracy', desc: 'Years of experience ensure every detail is covered, leaving no room for costly errors.' },
  { icon: faCogs, title: 'Tailored Solutions', desc: 'Estimations customized for your specific job — from small residential builds to large commercial projects.' },
  { icon: faClock, title: 'Time Saving', desc: 'Save hours of manual calculations and focus on building. We handle the intricate details.' },
  { icon: faCoins, title: 'Cost-Effective', desc: 'Accurate takeoffs and budgeting support reduce material waste and keep your project on budget.' },
];

const WHO_BENEFITS = [
  { icon: faHardHat, title: 'General Contractors', desc: 'Precise lumber estimates that keep construction timelines and budgets on track.' },
  { icon: faTools, title: 'Subcontractors', desc: 'Detailed counts and cost projections to focus on quality craftsmanship.' },
  { icon: faBriefcase, title: 'Project Managers', desc: 'Full control over project costs with real-time material requirement data.' },
  { icon: faBuilding, title: 'Developers', desc: 'Comprehensive takeoffs that improve planning and minimize delays.' },
  { icon: faDraftingCompass, title: 'Architects', desc: 'Reliable estimates aligned with your design vision for smooth project delivery.' },
  { icon: faChartLine, title: 'Engineers', desc: 'Precise technical estimations ensuring structural integrity and material efficiency.' },
  { icon: faCalculator, title: 'Quantity Surveyors', desc: 'Detailed material breakdowns that enhance cost management and budget delivery.' },
];

export default function Lumber() {
  return (
    <div className="svc-page">
      <section className="svc-hero">
        <div className="svc-hero-overlay" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="svc-hero-inner">
          <p className="svc-breadcrumb"><Link to="/">Home</Link><span>/</span>Lumber Estimation</p>
          <h1>Lumber Estimation</h1>
          <p className="svc-hero-sub">Accurate, reliable &amp; tailored for your projects — from framing to finish.</p>
          <div className="svc-hero-btns">
            <Link to="/schedule-meeting" className="svc-btn-primary"><FontAwesomeIcon icon={faCalendarCheck} /> Schedule a Meeting</Link>
            <Link to="/login" className="svc-btn-secondary">Register Portal <FontAwesomeIcon icon={faArrowRightLong} /></Link>
          </div>
        </div>
      </section>
      <section className="svc-intro">
        <div className="svc-container svc-intro-grid">
          <div className="svc-intro-text">
            <span className="svc-label">About This Service</span>
            <h2>Expert Lumber Estimation for Every Build</h2>
            <p>At Surya Group, we specialize in delivering precise and comprehensive lumber estimation services for contractors and construction specialists. Whether residential, commercial, or industrial, our expertise ensures you have a clear understanding of every lumber material required.</p>
            <ul className="svc-intro-checks">
              <li><FontAwesomeIcon icon={faCheckCircle} /> Fast turnaround on all estimates</li>
              <li><FontAwesomeIcon icon={faCheckCircle} /> Blueprint-based precision takeoffs</li>
              <li><FontAwesomeIcon icon={faCheckCircle} /> All lumber species &amp; engineered products</li>
            </ul>
          </div>
          <div className="svc-intro-img">
            <img loading="lazy" decoding="async" width="640" height="360" src={HERO_IMG} alt="Lumber estimation service" />
            <div className="svc-intro-badge"><strong>500+</strong><span>Projects Completed</span></div>
          </div>
        </div>
      </section>
      <section className="svc-offers">
        <div className="svc-container">
          <div className="svc-section-header">
            <span className="svc-label">What We Offer</span>
            <h2>Our Lumber Estimation Services</h2>
            <p>Four pillars that make your project planning accurate and stress-free.</p>
          </div>
          <div className="svc-offers-grid">
            {OFFERS.map(({ icon, number, title, desc, items }) => (
              <div key={number} className="svc-offer-card">
                <div className="svc-offer-top"><div className="svc-offer-icon"><FontAwesomeIcon icon={icon} /></div><span className="svc-offer-num">{number}</span></div>
                <h3>{title}</h3><p>{desc}</p>
                <ul>{items.map(i => <li key={i}><FontAwesomeIcon icon={faCheckCircle} />{i}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="svc-why">
        <div className="svc-container">
          <div className="svc-section-header svc-section-header--light">
            <span className="svc-label svc-label--light">Why Us</span>
            <h2>Why Choose Our Lumber Estimation?</h2>
            <p>Proven reasons contractors across North America trust Surya Group.</p>
          </div>
          <div className="svc-why-grid">
            {WHY_US.map(({ icon, title, desc }) => (
              <div key={title} className="svc-why-card"><div className="svc-why-icon"><FontAwesomeIcon icon={icon} /></div><h3>{title}</h3><p>{desc}</p></div>
            ))}
          </div>
        </div>
      </section>
      <section className="svc-who">
        <div className="svc-container">
          <div className="svc-section-header">
            <span className="svc-label">Who Benefits</span>
            <h2>Who Can Use This Service?</h2>
            <p>Our lumber estimation service is built for every construction professional.</p>
          </div>
          <div className="svc-who-grid">
            {WHO_BENEFITS.map(({ icon, title, desc }) => (
              <div key={title} className="svc-who-card"><div className="svc-who-icon"><FontAwesomeIcon icon={icon} /></div><h3>{title}</h3><p>{desc}</p></div>
            ))}
          </div>
        </div>
      </section>
      <EstimatingGuidance trade="lumber" />
      <section className="svc-cta">
        <div className="svc-container">
          <div className="svc-cta-box">
            <h2>Get Started Today!</h2>
            <p>Let us handle the complex task of lumber estimation while you focus on delivering exceptional construction outcomes. Contact us today for a free consultation.</p>
            <div className="svc-cta-btns">
              <Link to="/schedule-meeting" className="svc-btn-primary"><FontAwesomeIcon icon={faCalendarCheck} /> Schedule a Meeting</Link>
              <Link to="/contact-us" className="svc-btn-ghost">Contact Us <FontAwesomeIcon icon={faArrowRightLong} /></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
